import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { GreenScoreBadge } from "@/components/public/green-score-badge"
import PricesCard from "@/components/public/postcode-report/prices-card"
import CrimeCard from "@/components/public/postcode-report/crime-card"
import FloodCard from "@/components/public/postcode-report/flood-card"
import AmenitiesCard from "@/components/public/postcode-report/amenities-card"
import type {
  GreenScore,
  LandRegistryData,
  CrimeData,
  FloodData,
  AmenityData,
} from "@/types/public-data"

/* ------------------------------------------------------------------ */
/* Raw report shape from Rails (snake_case keys)                       */
/* ------------------------------------------------------------------ */

export interface RawLiveData {
  crime: {
    total_crimes: number
    month: string
    categories: Array<{ category: string; count: number }>
  } | null
  property: {
    sold_prices: Array<{ price: number; date: string; street: string }>
    price_index: Array<{ date: string; average_price: number }>
    current_average: number | null
  } | null
  flood: {
    stations: Array<{ label: string; river_name?: string; status?: string }>
    station_count: number
    has_active_warnings: boolean
  } | null
  amenities: {
    schools: Array<{ name: string; type?: string }>
    parks: Array<{ name: string }>
    restaurants: Array<{ name: string; cuisine?: string }>
    shops: number
    transport_stops: number
    cycleways: number
  } | null
  green_score: {
    total: number
    breakdown: {
      green_space: number
      flood_risk: number
      conservation: number
      walkability: number
      cycling: number
    }
  } | null
  [key: string]: unknown
}

/* ------------------------------------------------------------------ */
/* Mappers: snake_case -> camelCase                                     */
/* ------------------------------------------------------------------ */

function mapCrime(raw: RawLiveData["crime"]): CrimeData | null {
  if (!raw) return null
  return {
    totalCrimes: raw.total_crimes,
    month: raw.month,
    categories: raw.categories,
  }
}

function mapProperty(raw: RawLiveData["property"]): LandRegistryData | null {
  if (!raw) return null
  return {
    soldPrices: raw.sold_prices,
    priceIndex: (raw.price_index ?? []).map((p) => ({
      date: p.date,
      averagePrice: p.average_price,
    })),
    averagePrice: raw.current_average,
  }
}

function mapFlood(raw: RawLiveData["flood"]): FloodData | null {
  if (!raw) return null
  return {
    stations: raw.stations.map((s) => ({
      label: s.label,
      riverName: s.river_name,
      status: s.status,
    })),
    stationCount: raw.station_count,
    hasActiveWarnings: raw.has_active_warnings,
  }
}

function mapAmenities(raw: RawLiveData["amenities"]): AmenityData | null {
  if (!raw) return null
  return {
    schools: raw.schools,
    parks: raw.parks,
    restaurants: raw.restaurants,
    shops: raw.shops,
    transportStops: raw.transport_stops,
    cycleways: raw.cycleways,
  }
}

function mapGreenScore(raw: RawLiveData["green_score"]): GreenScore | null {
  if (!raw) return null
  return {
    total: raw.total,
    breakdown: {
      greenSpace: raw.breakdown.green_space,
      floodRisk: raw.breakdown.flood_risk,
      conservation: raw.breakdown.conservation,
      walkability: raw.breakdown.walkability,
      cycling: raw.breakdown.cycling,
    },
  }
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface LiveDataSectionProps {
  data: RawLiveData | null
}

export default function LiveDataSection({ data }: LiveDataSectionProps) {
  if (!data) return null

  const crime = mapCrime(data.crime)
  const property = mapProperty(data.property)
  const flood = mapFlood(data.flood)
  const amenities = mapAmenities(data.amenities)
  const greenScore = mapGreenScore(data.green_score)

  const hasAnyData = crime || property || flood || amenities

  if (!hasAnyData) return null

  return (
    <section className="border-t border-stone px-6 py-16">
      <div className="mx-auto max-w-3xl">
        <ScrollReveal>
          <div className="flex items-center gap-4">
            <h2 className="font-display text-2xl font-bold text-charcoal">
              Live local data<span className="text-red">.</span>
            </h2>
            {greenScore && <GreenScoreBadge score={greenScore} size="sm" />}
          </div>
          <p className="mt-2 text-sm text-slate">
            Real-time neighbourhood data sourced from official UK databases.
          </p>
        </ScrollReveal>

        <ScrollReveal stagger className="mt-8 grid gap-6 md:grid-cols-2">
          {property && <PricesCard data={property} />}
          {crime && <CrimeCard data={crime} />}
          {flood && <FloodCard data={flood} />}
          {amenities && <AmenitiesCard data={amenities} />}
        </ScrollReveal>

        <p className="mt-6 text-center text-xs text-slate">
          Data sources: postcodes.io, Police.uk, HM Land Registry, Environment Agency,
          OpenStreetMap (Overpass), Planning Data (DLUHC).
        </p>
      </div>
    </section>
  )
}
